/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 24-09-2026
 *
 * De ingang van het testharnas (`tests/browsertests/` in de devrepo): de services van de applicatie,
 * per stuk op te vragen. `App` zet hem in een development-build op `window.dlTest`; een productiebuild
 * maakt hem nooit aan.
 *
 * Elke methode vraagt de service pas bij aanroep aan de `Injector`, zodat de testingang zelf niets
 * eerder aanmaakt dan de applicatie dat doet. Het harnas bindt de methoden van het prototype (zie
 * `installeerIngang` in `harnas.mjs`), dus het zijn gewone methoden en geen pijlfuncties of getters.
 * Tot golf 4f deed de migratie-interface dit werk, naast zijn rol als brug naar AngularJS.
 */
import {Injectable, Injector, inject} from '@angular/core';
import {DlModel} from '../model/dl-model';
import {OpslagService} from '../model/opslag.service';
import {ModelbeheerService} from '../model/modelbeheer.service';
import {SamenwerkprotocolService} from '../samenwerking/samenwerkprotocol.service';
import {ApiService} from '../backend/api.service';
import {SimulatieService} from '../simulatie/simulatie.service';
import {SimfeedbackEngineService} from '../engines/simfeedback-engine.service';
import {BouwsuggestieEngineService} from '../engines/bouwsuggestie-engine.service';
import {NormService} from '../engines/norm.service';
import {CanvasregistratieService} from '../canvas/canvasregistratie.service';
import {WeergaveInstellingenService} from '../../services/weergave-instellingen.service';
import {VideoStatusService} from '../../services/video-status.service';
import {CondtagService} from '../../services/condtag.service';
import {SessieService} from '../../services/sessie.service';
import {ChatService} from '../../services/chat.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {FeedbackbalkService} from '../../services/feedbackbalk.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import {ToastService} from '../../services/toast.service';
import {Dashboardv1Service} from '../../services/dashboardv1.service';
import {Dashboardv2Service} from '../../services/dashboardv2.service';

@Injectable({providedIn: 'root'})
export class DlTestingangService {

    /**
     * Waar de services vandaan komen
     */
    private readonly injector = inject(Injector);

    /**
     * Het gedeelde modeldocument
     */
    getModel(): DlModel {
        return this.injector.get(DlModel);
    }

    /**
     * De opslagketen: de stand, de undo-buffer, de actielog en de snapshots
     */
    getOpslag(): OpslagService {
        return this.injector.get(OpslagService);
    }

    /**
     * De samenwerk-baas: het protocolverkeer en de acties
     */
    getSamenwerkprotocol(): SamenwerkprotocolService {
        return this.injector.get(SamenwerkprotocolService);
    }

    /**
     * Het modelbeheer: nieuw, laden, kopiëren en de opstart na het inloggen
     */
    getModelbeheer(): ModelbeheerService {
        return this.injector.get(ModelbeheerService);
    }

    /**
     * De weergave-instellingen: beginnersmodus en tekstbeschrijving
     */
    getWeergaveInstellingen(): WeergaveInstellingenService {
        return this.injector.get(WeergaveInstellingenService);
    }

    /**
     * De stand van de instructievideo
     */
    getVideoStatus(): VideoStatusService {
        return this.injector.get(VideoStatusService);
    }

    /**
     * De conditiesets
     */
    getCondtagService(): CondtagService {
        return this.injector.get(CondtagService);
    }

    /**
     * De backendlaag
     */
    getApi(): ApiService {
        return this.injector.get(ApiService);
    }

    /**
     * De sessie van de ingelogde gebruiker
     */
    getSessie(): SessieService {
        return this.injector.get(SessieService);
    }

    /**
     * De chat
     */
    getChat(): ChatService {
        return this.injector.get(ChatService);
    }

    /**
     * Of er gesimuleerd en gegenereerd wordt
     */
    getSimulatieStatus(): SimulatieStatusService {
        return this.injector.get(SimulatieStatusService);
    }

    /**
     * De aansturing van de simulatie: vragen, wachten en toepassen
     */
    getSimulatie(): SimulatieService {
        return this.injector.get(SimulatieService);
    }

    /**
     * De feedbackbalk
     */
    getFeedbackbalk(): FeedbackbalkService {
        return this.injector.get(FeedbackbalkService);
    }

    /**
     * De geselecteerde fout, suggestie of simfeedback
     */
    getFeedbackSelectie(): FeedbackSelectieService {
        return this.injector.get(FeedbackSelectieService);
    }

    /**
     * De stand van de samenwerking: rol, werkmodus en aanwezigen
     */
    getSamenwerking(): SamenwerkingService {
        return this.injector.get(SamenwerkingService);
    }

    /**
     * De toastmelding
     */
    getToast(): ToastService {
        return this.injector.get(ToastService);
    }

    /**
     * Het eerste docentendashboard
     */
    getDashboardv1(): Dashboardv1Service {
        return this.injector.get(Dashboardv1Service);
    }

    /**
     * Het tweede docentendashboard
     */
    getDashboardv2(): Dashboardv2Service {
        return this.injector.get(Dashboardv2Service);
    }

    /**
     * De simfeedback-engine
     */
    getSimfeedbackEngine(): SimfeedbackEngineService {
        return this.injector.get(SimfeedbackEngineService);
    }

    /**
     * De bouwsuggestie-engine
     */
    getBouwsuggestieEngine(): BouwsuggestieEngineService {
        return this.injector.get(BouwsuggestieEngineService);
    }

    /**
     * De norm: telling, interpretaties en normmatch
     */
    getNorm(): NormService {
        return this.injector.get(NormService);
    }

    /**
     * De registratie van het model- en het simcanvas; het harnas leest er `getCanvas(view)` uit
     */
    getCanvasregistratie(): CanvasregistratieService {
        return this.injector.get(CanvasregistratieService);
    }
}
